import { useEffect, useRef } from 'react';
import { useScopedStorage, useScopedStorageReady, useStorageScope } from './UserDeviceContext';
import { StorageMigrationManager } from './StorageMigrationManager';
import { ThemeManager } from '@/lib/theme/ThemeManager';
import { useTheme } from '@/lib/theme/ThemeProvider';
import { NotificationPreferencesManager } from '@/lib/services/NotificationPreferencesManager';
import { PreferredNameManager } from '@/lib/profile/PreferredNameManager';
import { ProfilePictureManager } from '@/lib/profile/ProfilePictureManager';
import { CoverImageManager } from '@/lib/home/CoverImageManager';

/**
 * StorageInitializer
 * Component that runs storage migration and configures singleton managers
 * when scoped storage becomes available or user changes.
 */
export function StorageInitializer() {
  const storage = useScopedStorage();
  const isReady = useScopedStorageReady();
  const scope = useStorageScope();
  const { refreshPreference } = useTheme();

  // Track the last initialized scope to detect user changes
  const lastScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isReady || !storage) {
      return;
    }

    // Create a scope identifier to detect changes
    const currentScopeId = `${scope.userId ?? 'guest'}:${scope.deviceId}`;
    
    // Skip if already initialized for this scope
    if (lastScopeRef.current === currentScopeId) {
      return;
    }

    const initializeStorage = async () => {
      try {
        console.log('[StorageInitializer] Initializing storage for scope:', currentScopeId);

        // Run migration if needed
        const migrationManager = new StorageMigrationManager(storage);
        const migrationResult = await migrationManager.migrateIfNeeded();
        
        if (migrationResult.migratedKeys.length > 0) {
          console.log('[StorageInitializer] Migration completed:', {
            migratedKeys: migrationResult.migratedKeys,
            skippedKeys: migrationResult.skippedKeys,
          });
        }

        // Configure singleton managers with scoped storage
        const themeManager = ThemeManager.getInstance();
        themeManager.setStorage(storage);
        await themeManager.initialize();
        refreshPreference();

        const notificationPrefsManager = NotificationPreferencesManager.getInstance();
        notificationPrefsManager.setStorage(storage);
        await notificationPrefsManager.initialize();

        const preferredNameManager = PreferredNameManager.getInstance();
        preferredNameManager.setStorage(storage);
        await preferredNameManager.initialize();

        const profilePictureManager = ProfilePictureManager.getInstance();
        profilePictureManager.setStorage(storage);
        await profilePictureManager.initialize();

        const coverImageManager = CoverImageManager.getInstance();
        coverImageManager.setStorage(storage);
        await coverImageManager.initialize();

        // Mark this scope as initialized
        lastScopeRef.current = currentScopeId;

        console.log('[StorageInitializer] Storage initialization complete');
      } catch (error) {
        console.error('[StorageInitializer] Failed to initialize storage:', error);
      }
    };

    initializeStorage();
  }, [isReady, storage, scope.userId, scope.deviceId, refreshPreference]);

  // This component doesn't render anything
  return null;
}
